import React from 'react';
import { Box, Button, Typography, Paper, Alert, Divider } from '@mui/material';
import { 
  RefreshRounded as RetryIcon, 
  ErrorOutline as ErrorIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const ErrorContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  minWidth: '100vw',
  background: 'radial-gradient(circle at center, #c4262e 0%, #a21e26 50%, #7a161c 100%)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 30%, rgba(196, 38, 46, 0.15) 0%, transparent 50%), 
      radial-gradient(circle at 80% 70%, rgba(45, 54, 58, 0.1) 0%, transparent 50%)
    `,
  }
}));

const ErrorPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  minWidth: '90vw',
  maxWidth: '550px',
  textAlign: 'center',
  borderRadius: theme.spacing(3),
  boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
  animation: `${fadeInScale} 0.5s ease-out`,
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.up('sm')]: {
    minWidth: 450,
  }
}));

const ErrorIconWrapper = styled(Box)(() => ({
  animation: `${shake} 0.8s ease-in-out`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
  boxShadow: '0 10px 25px rgba(238, 90, 111, 0.3)',
}));

interface LoginErrorScreenProps {
  error: string;
  onRetry: () => void;
}

const LoginErrorScreen: React.FC<LoginErrorScreenProps> = ({ error, onRetry }) => {
  const getErrorCategory = (errorMessage: string): { title: string; description: string } => {
    const lowerError = errorMessage.toLowerCase();
    
    if (lowerError.includes('unauthorized') || lowerError.includes('access denied')) {
      return {
        title: 'Access Denied',
        description: 'You do not have permission to access this application. Please contact your system administrator.'
      };
    }
    
    if (lowerError.includes('token') || lowerError.includes('expired')) {
      return {
        title: 'Session Expired',
        description: 'Your authentication session has expired. Please sign in again.'
      };
    }
    
    if (lowerError.includes('network') || lowerError.includes('connection')) {
      return {
        title: 'Connection Error',
        description: 'Unable to connect to the authentication server. Please check your internet connection and try again.'
      };
    }
    
    return {
      title: 'Authentication Failed',
      description: 'We encountered an issue while trying to sign you in. Please try again or contact support if the problem persists.'
    };
  };

  const { title, description } = getErrorCategory(error);

  return (
    <ErrorContainer>
      <ErrorPaper elevation={6}>
        <ErrorIconWrapper>
          <ErrorIcon sx={{ fontSize: 45, color: 'white' }} />
        </ErrorIconWrapper>

        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 700, 
            color: '#2d363a',
            mb: 1 
          }}
        >
          {title}
        </Typography>

        <Alert 
          severity="error" 
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {error}
          </Typography>
        </Alert>

        <Typography 
          variant="body1" 
          sx={{ 
            color: '#5a5a5a',
            lineHeight: 1.6,
            px: 2
          }}
        >
          {description}
        </Typography>

        <Divider sx={{ width: '100%', my: 1 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<RetryIcon />}
            onClick={onRetry}
            sx={{ 
              minWidth: 200,
              bgcolor: '#c4262e',
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(196, 38, 46, 0.3)',
              '&:hover': {
                bgcolor: '#a21e26',
                boxShadow: '0 6px 16px rgba(196, 38, 46, 0.4)',
              }
            }}
          >
            Try Again
          </Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
            BCT - Business Case Tracker
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Edwards Vaccum © {new Date().getFullYear()}
          </Typography>
        </Box>
      </ErrorPaper>
    </ErrorContainer>
  );
};

export default LoginErrorScreen;